import React, {useEffect, useState} from "react";
import {useThemeMode, systemMode} from "../../../_metronic/partials/layout/theme-mode/ThemeModeProvider";

// --- Helper Components & Functions ---

const RunningTimer = ({startDate}: {startDate: string}) => {
    const [timeString, setTimeString] = useState('');

    useEffect(() => {
        if (!startDate) return;

        const update = () => {
            const now = new Date().getTime();
            const start = new Date(startDate).getTime();
            const diff = now - start;

            if (diff < 0) return;

            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            let str = '';
            if (days > 0) str += `${days}d `;
            if (hours > 0) str += `${hours}h `;
            str += `${minutes}m ${seconds}s`;

            setTimeString(str);
        };

        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [startDate]);

    return <span className="font-monospace fw-bold ms-1 text-primary">{timeString}</span>;
};

const formatDuration = (start: string, end: string) => {
    if (!start || !end) return 'N/A';
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).getTime();
    const diff = endTime - startTime;
    
    if (diff < 0) return 'N/A';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    let str = '';
    if (days > 0) str += `${days}d `;
    if (hours > 0) str += `${hours}h `;
    str += `${minutes}m ${seconds}s`;
    return str || '0s';
};

const StatusBadge = ({ status }: {status: string}) => {
    const styles: any = {
        'OPEN': { color: 'primary', label: 'Open', icon: 'bi-hourglass' },
        'IN_PROGRESS': { color: 'warning', label: 'In Progress', icon: 'bi-gear-wide-connected' },
        'CLOSED': { color: 'success', label: 'Closed', icon: 'bi-check-circle-fill' }
    };
    const style = styles[status] || { color: 'secondary', label: status, icon: 'bi-circle' };
    
    return (
        <span className={`badge badge-light-${style.color} fs-7 fw-bold px-3 py-2 d-flex align-items-center`}>
            <i className={`bi ${style.icon} me-2 text-${style.color}`}></i>
            {style.label}
        </span>
    );
};

const JobTypeBadge = ({ type }: {type: string}) => {
        const types: any = {
        'FREE_SERVICE': 'Free Service',
        'PAID_SERVICE': 'Paid Service',
        'RUNNING_REPAIR': 'Running Repair',
        'ACCIDENTAL_REPAIR': 'Accidental Repair'
    };
    return <span className="text-gray-600 fw-bold fs-7">{types[type] || type}</span>;
};

// --- Main Component ---

interface JobCardItemProps {
    card: any;
    onDetailsClick: (id: string) => void;
    onEditClick: (id: string) => void;
    onDeleteClick: (id: string) => void;
}

export const JobCardItem: React.FC<JobCardItemProps> = ({
    card,
    onDetailsClick,
    onEditClick,
    onDeleteClick
}) => {
    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');
    const treatments = card.selectedTreatments || [];
    const treatmentCount = treatments.length;

    const counts = treatments.reduce((acc: any, t: any) => {
        const status = t.status || 'TODO';
        if (status === 'COMPLETE') acc.complete++;
        else if (status === 'IN_PROGRESS') acc.progress++;
        else acc.todo++;
        return acc;
    }, { todo: 0, progress: 0, complete: 0 });

    const getPercent = (count: number) => (treatmentCount > 0 ? (count / treatmentCount) * 100 : 0);

    // Only apply minWidth on non-mobile screens (> 576px)
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 576);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-4" style={isMobile ? {} : { minWidth: '420px' }}>
            <div
                className="card h-100 border border-gray-300 shadow-sm hover-elevate-up cursor-pointer"
                onClick={() => onDetailsClick(card.id)}
                style={{
                    cursor: 'pointer',
                    borderRadius: '12px',
                    transition: 'all 0.3s ease',
                    backgroundColor: isDarkMode ? '#1e1e2d' : ''
                }}>
                <div className="card-body p-4">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <StatusBadge status={card.status} />
                    <div className="dropdown">
                        {card.status !== 'CLOSED' && (
                            <>
                                <button className="btn btn-icon btn-sm btn-light-primary w-30px h-30px"
                                        onClick={(e) => { e.stopPropagation(); onEditClick(card.id); }}>
                                    <i className="bi bi-pencil-fill fs-5"></i>
                                </button>
                                <button className="btn btn-icon btn-sm btn-light-danger w-30px h-30px ms-2"
                                        onClick={(e) => { e.stopPropagation(); onDeleteClick(card.id); }}>
                                    <i className="bi bi-trash-fill fs-5"></i>
                                </button>
                            </>
                        )}
                    </div>
                </div>

                    {/* Vehicle Number - Large and Prominent */}
                    <div className="mb-3">
                        <div className="d-flex align-items-center mb-1">
                            {card.oemName ? (
                                <img 
                                    src={`/media/car/${card.oemName}.png`} 
                                    alt={card.oemName} 
                                    className="w-30px me-2" 
                                    onError={(e: any) => {e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline-block';}} 
                                />
                            ) : null}
                            <i className="bi bi-car-front-fill text-primary fs-4 me-2" style={{display: card.oemName ? 'none' : 'inline-block'}}></i>
                            <span className="fw-bolder text-gray-900 fs-3">{card.vehicleNo}</span>
                        </div>
                        <div className="d-flex flex-column ms-1">
                            <div className="text-gray-800 fs-7 fw-bold">
                                {card.vehicleModelName || 'Model N/A'}
                            </div>
                            <div className="text-gray-600 fs-8 fw-bold">
                                RO: {card.roNumber}
                            </div>
                        </div>
                    </div>

                    <div className="separator separator-dashed my-3"></div>

                    {/* Job Type */}
                    <div className="mb-3">
                        <span className="text-gray-500 fs-9 fw-bold text-uppercase d-block mb-1">Job Type</span>
                        <JobTypeBadge type={card.jobType} />
                    </div>

                    {/* Treatments Count & Progress */}
                    <div className="mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="d-flex align-items-center">
                                <i className="bi bi-wrench-adjustable-circle text-info fs-5 me-2"></i>
                                <span className="text-gray-700 fw-bold fs-7">
                                    {treatmentCount} Treatment{treatmentCount !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <span className="text-muted fs-9 fw-bold">
                                {counts.complete}/{treatmentCount} Done
                            </span>
                        </div>
                        <div className="progress h-6px w-100 bg-gray-200 rounded mb-2">
                            <div 
                                className="progress-bar bg-success" 
                                role="progressbar" 
                                style={{width: `${getPercent(counts.complete)}%`}}
                                title={`Complete: ${counts.complete}`}
                            ></div>
                            <div 
                                className="progress-bar bg-warning" 
                                role="progressbar" 
                                style={{width: `${getPercent(counts.progress)}%`}}
                                title={`In Progress: ${counts.progress}`}
                            ></div>
                            <div 
                                className="progress-bar bg-primary" 
                                role="progressbar" 
                                style={{width: `${getPercent(counts.todo)}%`}}
                                title={`To Do: ${counts.todo}`}
                            ></div>
                        </div>
                        {card.deletedTreatments?.length > 0 && (
                            <div className="d-flex align-items-center mt-1">
                                <i className="bi bi-trash text-danger fs-9 me-1"></i>
                                <span className="text-danger fs-9 fw-semibold">
                                    {card.deletedTreatments.length} deleted treatment{card.deletedTreatments.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Branch & Supervisor/Advisor */}
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-start">
                            {/* Left Side: Dealer & Branch */}
                            <div className="d-flex align-items-start">
                                <i className="bi bi-building text-success fs-5 me-2 mt-1"></i>
                                <div className="d-flex flex-column">
                                    {card.dealerName && (
                                        <span className="text-gray-900 fw-bolder fs-7">{card.dealerName}</span>
                                    )}
                                    <span className="text-gray-800 fw-bold fs-8">
                                        {card.branchName || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            {/* Right Side: Advisor & Supervisor */}
                            <div className="d-flex flex-column align-items-end text-end ms-2">
                                    {card.advisorName && (
                                    <span className="text-gray-600 fs-9 mb-1">
                                        <i className="bi bi-person-check fs-8 me-1 text-primary"></i>
                                        {card.advisorName}
                                    </span>
                                )}
                                {card.supervisorName && card.supervisorName.length > 0 && (
                                    <span className="text-gray-600 fs-9">
                                        <i className="bi bi-person-badge fs-8 me-1 text-info"></i>
                                        {Array.isArray(card.supervisorName) ? card.supervisorName.join(', ') : card.supervisorName}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Committed Date & Timer */}
                    <div className="mt-3 pt-3 border-top border-gray-300">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <span className="text-gray-500 fs-9 fw-bold text-uppercase">Committed</span>
                            <span className="text-gray-800 fw-bold fs-8">
                                {card.committedDate ? new Date(card.committedDate).toLocaleString([], {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                }) : 'N/A'}
                            </span>
                        </div>
                        
                        <div className="d-flex align-items-center justify-content-between bg-light rounded p-2">
                            <span className="text-gray-600 fs-9 fw-bold text-uppercase">
                                {card.status === 'CLOSED' ? 'Total Time' : 'Running Time'}
                            </span>
                            {card.status === 'CLOSED' ? (
                                <span className="font-monospace fw-bold fs-8 text-success">
                                    {formatDuration(card.createdOn, card.endDate || card.updatedOn)}
                                </span>
                            ) : (
                                <span className="fs-8">
                                    <RunningTimer startDate={card.createdOn} />
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
